import { gql, useLazyQuery, useQuery } from '@apollo/client';
import type { Statistics } from '@interfaces/stats';
import { PageObjectType } from '@ovComponents/5-page/pageConfiguration/pageConfigurationTypes';
import { ObjectType } from '@utils/stats/statsObjectType';
import { useContext, useEffect, useState } from 'react';
import {
  getTranslations,
  TRANSLATED_STRING_FIELDS,
} from '../assets/i18n/config';
import { determinePageTypeFromRoute } from '../utils/routeUtils';
import { UserContext } from './userContextProvider';

export { ObjectType } from '@utils/stats/statsObjectType';

export const objectTypeMapping = (objectType: PageObjectType): ObjectType =>
  ({
    [PageObjectType.INDIVIDUAL]: ObjectType.USER,
    [PageObjectType.NON_INDIVIDUAL]: ObjectType.USER,
    [PageObjectType.HOUSEHOLD]: ObjectType.CLIENT_GROUP,
    [PageObjectType.GOAL]: ObjectType.GOAL,
    [PageObjectType.SUB_ACCOUNT]: ObjectType.SUB_ACCOUNT,
    [PageObjectType.ACCOUNT]: ObjectType.ACCOUNT,
    [PageObjectType.ACCOUNT_GROUP]: ObjectType.ACCOUNT_GROUP,
  })[objectType];

export enum BookValueType {
  USER = 'userBookValue',
  GOAL = 'goalBookValue',
  ACCOUNT = 'accountBookValue',
  SUB_ACCOUNT = 'subAccountBookValue',
  CLIENT_GROUP = 'householdClientGroupBookValue',
}

export enum PerformancePeriods {
  ALL_TIME = 'ALL_TIME',
  FIVE_YEARS = 'FIVE_YEARS',
  MONTH_TO_DATE = 'MONTH_TO_DATE',
  ONE_MONTH = 'ONE_MONTH',
  ONE_YEAR = 'ONE_YEAR',
  QUARTER_TO_DATE = 'QUARTER_TO_DATE',
  SIX_MONTHS = 'SIX_MONTHS',
  THREE_MONTHS = 'THREE_MONTHS',
  THREE_YEARS = 'THREE_YEARS',
  YEAR_TO_DATE = 'YEAR_TO_DATE',
}

const CUSTODIAN_HOLDINGS_ATTRIBUTES = `#graphql
  financialProduct {
    id: ticker
    ticker
    isCash
    ric
    type
    translatedName { ${TRANSLATED_STRING_FIELDS} }
    primaryAssetClass{ riskLevel translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription { ${TRANSLATED_STRING_FIELDS} } }
    secondaryAssetClass { riskLevel translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription { ${TRANSLATED_STRING_FIELDS} } }
    tertiaryAssetClass { riskLevel translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription { ${TRANSLATED_STRING_FIELDS} } }
  }
  eodPriceCents
  adjustedCostBaseCents
  totalCents
  currentPriceCents
  quantity
  currency
  originalCurrency { currency totalCents currentPriceCents adjustedCostBaseCents eodPriceCents }
`;

const SHARED_CUSTODIAN_STATS = (
  fetchHistory?: boolean,
  currency?: string,
  fetchProjectedIncome?: boolean,
  _customFieldKeys?: string[],
) => {
  const { custodianConnection } = useContext(UserContext);
  const includeProjectedIncome =
    !!custodianConnection?.enableFetchCustodianProjectedIncome;
  const includeSnapShotHistory =
    custodianConnection?.enableFetchCustodianSnapshotHistory;

  return `#graphql
    custodianStatistics(input: {${currency ? `currency: ${currency},` : ''} startDate: $startDate, endDate: $endDate}) {
      marketValueCents
      currency
      foreignExchangeRates { from to rate }
      lastUpdatedAt
      simpleReturnAmountCents
      simpleReturnPercent
      moneyWeightedReturn
      timeWeightedReturn
      ${
        fetchProjectedIncome && includeProjectedIncome
          ? `#graphql
        projectedIncome { amountCents currency date financialProduct { translatedName { ${TRANSLATED_STRING_FIELDS} } } }
      `
          : ''
      }
      holdings { ${CUSTODIAN_HOLDINGS_ATTRIBUTES} }
      holdingsMissingRate { ${CUSTODIAN_HOLDINGS_ATTRIBUTES} }
      originalCurrencies { currency marketValueCents }
    }
    ${
      fetchHistory && includeSnapShotHistory
        ? `
      custodianSnapshotHistory(input: {${currency ? `currency: ${currency},` : ''} startDate: $startDate, endDate: $endDate}) {
        date
        marketValueCents
        netContributionCents
        currency
      }
    `
        : ''
    }
  `;
};

const HOLDINGS_ATTRIBUTES = (customFieldKeys?: string[]) => `#graphql
  financialProduct {
    id: stringId
    translatedUrl { ${TRANSLATED_STRING_FIELDS} }
    translatedName { ${TRANSLATED_STRING_FIELDS} }
    ticker
    isCash
    ric
    name
    currentPriceCents
    taxRank
    type
    fundSeries
    primaryAssetClass{ id: stringId key riskLevel translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription { ${TRANSLATED_STRING_FIELDS} } }
    secondaryAssetClass { id: stringId key riskLevel translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription { ${TRANSLATED_STRING_FIELDS} } }
    tertiaryAssetClass { id: stringId key riskLevel translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription { ${TRANSLATED_STRING_FIELDS} } }
  }
  eodPriceCents
  localCost
  nativeCost
  localBookValue
  nativeBookValue
  custodianBookValueCents
  adjustedCostBaseCents
  totalCents
  expectedTotalCents
  currentPriceCents
  quantity
  currency
  originalCurrency { currency totalCents currentPriceCents adjustedCostBaseCents }
  accountTaxLots { id priceCents quantity date }
  userBookValue
  goalBookValue
  accountBookValue
  subAccountBookValue
  householdClientGroupBookValue
  ${
    (customFieldKeys ?? []).length > 0
      ? `customFields(keys: ${JSON.stringify(customFieldKeys)}) {
    key
    value
    customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription { ${TRANSLATED_STRING_FIELDS} } type format enableAggregation }
    selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
  }`
      : ''
  }
`;

const SHARED_STATS = (
  forUserId?: string,
  customFieldKeys?: string[],
) => `#graphql
  statistics(input: { currency: $currency }, ${forUserId ? `forUserId:"${forUserId}"` : ''}) {
    marketValueCents
    moneyAvailableCents
    lastUpdatedAt
    currency
    foreignExchangeRates{ from to rate }
    holdingsMissingRate { ${HOLDINGS_ATTRIBUTES(customFieldKeys)} }
    simpleReturnAmount(startDate: $startDate, endDate: $endDate, period: $period)
    simpleReturnPercent(startDate: $startDate, endDate: $endDate, period: $period)
    moneyWeightedReturn(startDate: $startDate, endDate: $endDate, period: $period)
    timeWeightedReturn(startDate: $startDate, endDate: $endDate, period: $period)
    netContributionCents
    netContributionCentsInFiscalYear
    newContributionCents
    cashOnHoldToWithdrawCents
    cashOnHoldToTradeCents
    holdings { ${HOLDINGS_ATTRIBUTES(customFieldKeys)} }
    originalCurrencies {
      currency
      # TODO: Add originalCurrencies.marketValueCents in the stats-service
      # marketValueCents
    }
  }
`;

const USER_STATS = (
  useCustodianData?: string,
  fetchHistory?: boolean,
  currency?: string,
  fetchProjectedIncome?: boolean,
  customFieldKeys?: string[],
) => gql`
  query fetchUserStatistics($userId: SafeString!, $startDate: Date, $endDate: Date, ${!useCustodianData ? '$period: PerformancePeriods' : ''}, ${!useCustodianData ? '$currency: StatisticsCurrencyTypes' : ''}) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        ${useCustodianData ? SHARED_CUSTODIAN_STATS(fetchHistory, currency, fetchProjectedIncome, customFieldKeys) : SHARED_STATS(undefined, customFieldKeys)}
      }
    }
  }
`;

export const GOAL_STATS = (
  onlyUserId?: string,
  useCustodianData?: string,
  fetchHistory?: boolean,
  currency?: string,
  fetchProjectedIncome?: boolean,
  customFieldKeys?: string[],
) => gql`
  query fetchGoalStatistics($goalId: ObjectID!, $startDate: Date, $endDate: Date, ${!useCustodianData ? '$period: PerformancePeriods' : ''}, ${!useCustodianData ? '$currency: StatisticsCurrencyTypes' : ''}) {
    fetchGoal(goalId: $goalId) {
      goal {
        id
        targetAmountCents
        ${useCustodianData ? SHARED_CUSTODIAN_STATS(fetchHistory, currency, fetchProjectedIncome, customFieldKeys) : SHARED_STATS(onlyUserId, customFieldKeys)}
      }
    }
  }
`;

export const SUB_ACCOUNT_STATS = (customFieldKeys?: string[]) => gql`
  query fetchSubAccountStatistics($subAccountId: ObjectID!, $startDate: Date, $endDate: Date, $period: PerformancePeriods, $currency: StatisticsCurrencyTypes) {
    fetchSubAccount(subAccountId: $subAccountId) {
      subAccount { id ${SHARED_STATS(undefined, customFieldKeys)} }
    }
  }
`;

export const ACCOUNT_STATS = (
  useCustodianData?: string,
  fetchHistory?: boolean,
  currency?: string,
  fetchProjectedIncome?: boolean,
  customFieldKeys?: string[],
) => gql`
  query fetchAccountStatistics($accountId: SafeString!, $startDate: Date, $endDate: Date, ${!useCustodianData ? '$period: PerformancePeriods' : ''}, ${!useCustodianData ? '$currency: StatisticsCurrencyTypes' : ''}) {
    fetchAccount(input: { accountId: $accountId }) {
      account {
        id: stringId
        ${useCustodianData ? SHARED_CUSTODIAN_STATS(fetchHistory, currency, fetchProjectedIncome, customFieldKeys) : SHARED_STATS(undefined, customFieldKeys)}
        user {
          statistics { marketValueCents pendingCashCents }
        }
      }
    }
  }
`;

export const CLIENT_GROUP_STATS = (
  onlyUserId?: string,
  useCustodianData?: string,
  fetchHistory?: boolean,
  currency?: string,
  fetchProjectedIncome?: boolean,
  customFieldKeys?: string[],
) => gql`
  query fetchClientGroupStatistics($clientGroupId: SafeString!, $startDate: Date, $endDate: Date, ${!useCustodianData ? '$period: PerformancePeriods' : ''}, ${!useCustodianData ? '$currency: StatisticsCurrencyTypes' : ''}) {
    fetchClientGroup(input: { clientGroupId: $clientGroupId }) {
      clientGroup {
        id: stringId
        ${useCustodianData ? SHARED_CUSTODIAN_STATS(fetchHistory, currency, fetchProjectedIncome, customFieldKeys) : SHARED_STATS(onlyUserId, customFieldKeys)}
      }
    }
  }
`;

export const ACCOUNT_GROUP_STATS = (customFieldKeys?: string[]) => gql`
  query fetchAccountGroupStatistics($accountGroupId: ObjectID!, $startDate: Date, $endDate: Date, $period: PerformancePeriods, $currency: StatisticsCurrencyTypes) {
    fetchAccountGroup(accountGroupId: $accountGroupId) {
      accountGroup { id ${SHARED_STATS(undefined, customFieldKeys)} }
    }
  }
`;

export const FETCH_STATS_HISTORY = gql`
  query fetchStatsHistory($input: FetchStatsHistoryInput!) {
    fetchStatsHistory(input: $input) {
      totalCount
      # TODO: add originalCurrency to holdings snapshots
      snapshots {
        date
        marketValueCents
        netContributionCents
      }
    }
  }
`;

const queries: any = {
  Client: USER_STATS,
  Goal: GOAL_STATS,
  Account: ACCOUNT_STATS,
  SubAccount: SUB_ACCOUNT_STATS,
  ClientGroup: CLIENT_GROUP_STATS,
  AccountGroup: ACCOUNT_GROUP_STATS,
};

const field = {
  Client: 'user',
  Goal: 'goal',
  Account: 'account',
  SubAccount: 'subAccount',
  ClientGroup: 'clientGroup',
  AccountGroup: 'accountGroup',
};

export const historyDailyTypes = {
  Client: 'USERS_DAILY',
  Goal: 'GOALS_DAILY',
  Account: 'ACCOUNTS_DAILY',
  SubAccount: 'SUB_ACCOUNTS_DAILY',
  ClientGroup: 'CLIENT_GROUPS_DAILY',
  AccountGroup: 'ACCOUNT_GROUPS_DAILY',
};

const getQuery = ({
  type,
  onlyUserId,
  useCustodianData,
  fetchHistory,
  fetchProjectedIncome,
  currency,
  customFieldKeys,
}: {
  type: ObjectType;
  onlyUserId?: string;
  useCustodianData?: boolean;
  fetchHistory?: boolean;
  fetchProjectedIncome?: boolean;
  currency?: string;
  customFieldKeys?: string[];
}) => {
  if ([ObjectType.SUB_ACCOUNT, ObjectType.ACCOUNT_GROUP].includes(type))
    return queries[type](customFieldKeys);
  return [ObjectType.CLIENT_GROUP, ObjectType.GOAL].includes(type)
    ? queries[type](
        onlyUserId,
        useCustodianData,
        fetchHistory,
        currency,
        fetchProjectedIncome,
        customFieldKeys,
      )
    : queries[type](
        useCustodianData,
        fetchHistory,
        currency,
        fetchProjectedIncome,
        customFieldKeys,
      );
};

export const useStats = ({
  type,
  id,
  startDate,
  endDate,
  period,
  onlyUserId,
  useCustodianData,
  fetchHistory = true,
  fetchProjectedIncome,
  currency,
  skipErrorHandler = false,
  customFieldKeys,
}: {
  type: ObjectType;
  id: string;
  startDate?: string;
  endDate?: string;
  period?: PerformancePeriods;
  onlyUserId?: string;
  useCustodianData?: boolean;
  fetchHistory?: boolean;
  fetchProjectedIncome?: boolean;
  currency?: string;
  skipErrorHandler?: boolean;
  customFieldKeys?: string[];
}) => {
  if (![ObjectType.CLIENT_GROUP, ObjectType.GOAL].includes(type) && onlyUserId)
    throw new Error(
      'useStats: Only use onlyUserId with type CLIENT_GROUP or GOAL',
    );

  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [history, setHistory] = useState<any>([]);
  const [object, setObject] = useState<any>({});

  const variables = {
    Client: { userId: id },
    Goal: { goalId: id },
    Account: { accountId: id },
    SubAccount: { subAccountId: id },
    ClientGroup: { clientGroupId: id },
    AccountGroup: { accountGroupId: id },
  };

  const {
    data,
    loading,
    refetch,
    error: statsError,
  } = useQuery(
    getQuery({
      type,
      onlyUserId,
      useCustodianData,
      fetchHistory,
      fetchProjectedIncome,
      currency,
      customFieldKeys,
    }),
    {
      variables: {
        ...variables[type],
        startDate,
        endDate,
        period,
        currency,
        skipErrorHandler,
      },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  );

  const [
    fetchStatsHistory,
    {
      loading: statsHistoryLoading,
      refetch: historyRefetch,
      error: historyError,
    },
  ] = useLazyQuery(FETCH_STATS_HISTORY, {
    onCompleted: (historyData: any) => {
      setHistory(historyData.fetchStatsHistory.snapshots);
    },
  });

  const refetchAll = () => {
    refetch();
    if (!useCustodianData && fetchHistory) historyRefetch();
  };

  useEffect(() => {
    if (data) {
      const firstKey = Object.keys(data)[0];
      const statsObject = data[firstKey][field[type]];
      setObject(statsObject);

      if (useCustodianData) {
        statsObject.custodianStatistics?.holdings?.forEach((holding: any) => {
          if (holding.financialProduct.primaryAssetClass) {
            if (!holding.financialProduct.primaryAssetClass.translatedName) {
              holding.financialProduct.primaryAssetClass.translatedName =
                getTranslations({
                  fileName: 'shared',
                  translationPath: ['notClassified'],
                });
            }
            // i18n: Using .en as stable key for chart grouping - must be consistent across all users
            holding.financialProduct.primaryAssetClass.key =
              holding.financialProduct.primaryAssetClass.translatedName.en;
            holding.financialProduct.primaryAssetClass.id =
              holding.financialProduct.primaryAssetClass.translatedName.en;
          }
          if (holding.financialProduct.secondaryAssetClass) {
            if (!holding.financialProduct.secondaryAssetClass.translatedName) {
              holding.financialProduct.secondaryAssetClass.translatedName =
                getTranslations({
                  fileName: 'shared',
                  translationPath: ['notClassified'],
                });
            }
            // i18n: Using .en as stable key for chart grouping - must be consistent across all users
            holding.financialProduct.secondaryAssetClass.key =
              holding.financialProduct.secondaryAssetClass.translatedName.en;
            holding.financialProduct.secondaryAssetClass.id =
              holding.financialProduct.secondaryAssetClass.translatedName.en;
          }
          if (holding.financialProduct.tertiaryAssetClass) {
            if (!holding.financialProduct.tertiaryAssetClass.translatedName) {
              holding.financialProduct.tertiaryAssetClass.translatedName =
                getTranslations({
                  fileName: 'shared',
                  translationPath: ['notClassified'],
                });
            }
            // i18n: Using .en as stable key for chart grouping - must be consistent across all users
            holding.financialProduct.tertiaryAssetClass.key =
              holding.financialProduct.tertiaryAssetClass.translatedName.en;
            holding.financialProduct.tertiaryAssetClass.id =
              holding.financialProduct.tertiaryAssetClass.translatedName.en;
          }
        });
        if (fetchHistory) setHistory(statsObject.custodianSnapshotHistory);
      } else if (statsObject.statistics.simpleReturnAmount) {
        // The simpleReturnAmount is actually coming in cents.
        // TODO: update history-service to return simpleReturnAmountCents and deprecate simpleReturnAmount
        statsObject.statistics.simpleReturnAmountCents =
          statsObject.statistics.simpleReturnAmount;
      }
      setStatistics(
        useCustodianData
          ? statsObject.custodianStatistics
          : statsObject.statistics,
      );
    }
  }, [data, type, id, useCustodianData, fetchHistory]);

  useEffect(() => {
    if (!useCustodianData && fetchHistory) {
      fetchStatsHistory({
        variables: {
          input: {
            type: historyDailyTypes[type],
            id,
            currency: currency ?? 'CAD',
            filter: {
              ...(determinePageTypeFromRoute() === ObjectType.CLIENT_GROUP && {
                includeHousehold: true,
              }),
              afterDate: startDate,
              beforeDate: endDate,
              userId: onlyUserId,
            },
          },
        },
      });
    }
  }, [
    type,
    id,
    startDate,
    endDate,
    onlyUserId,
    currency,
    useCustodianData,
    fetchStatsHistory,
    fetchHistory,
  ]);

  return {
    statistics,
    loading,
    history,
    historyLoading: useCustodianData ? loading : statsHistoryLoading,
    refetchAll,
    object,
    statsError,
    historyError,
  };
};
